import { useEffect, useRef, useState } from "react";
import { magicMfaCodeSchema } from "@io-spoke/contracts/magicLinks";
import { useAsyncCallback } from "../../hooks/useAsyncCallback";
import { verifyMagicMfa } from "../../lib/magicLinkMfa";
import { setAuthTokens } from "../../lib/authStorage";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

type Props = { challenge: { session: string; expiresAt: string }; onComplete: () => void; onBack: () => void };
export function MagicTotpChallenge({ challenge, onComplete, onBack }: Props) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const version = useRef(0);
  useEffect(() => {
    mounted.current = true;
    const changed = () => { version.current += 1; setCode(""); setError("Your sign-in changed. Go back and request a new email link."); };
    window.addEventListener("auth:changed", changed);
    return () => { mounted.current = false; window.removeEventListener("auth:changed", changed); };
  }, []);
  const action = useAsyncCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    const attempt = version.current;
    event.preventDefault(); setError(null);
    const parsed = magicMfaCodeSchema.safeParse({ code: code.trim() });
    if (!parsed.success) { setError("Enter the six-digit code from your IO Spoke authenticator."); return; }
    if (Date.now() >= Date.parse(challenge.expiresAt)) { setError("This sign-in request expired. Go back and request a new email link."); return; }
    try {
      const result = await verifyMagicMfa(challenge.session, parsed.data.code);
      if (!mounted.current || version.current !== attempt) return;
      setCode("");
      setAuthTokens({ accessToken: result.accessToken, user: result.user }, false);
      onComplete();
    } catch (cause) {
      if (mounted.current && version.current === attempt) { setCode(""); setError(cause instanceof Error ? cause.message : "The code could not be verified. Try the next code."); }
    }
  });
  return (
    <form onSubmit={action.callback} className="space-y-4" noValidate>
      <p className="text-sm text-muted-foreground">Enter the code from your IO Spoke authenticator.</p>
      <p className="text-xs text-muted-foreground">Sign-in request expires <time dateTime={challenge.expiresAt}>{new Date(challenge.expiresAt).toLocaleTimeString()}</time>.</p>
      <div className="space-y-2">
        <Label htmlFor="magic-authenticator-code">Authenticator code</Label>
        <Input id="magic-authenticator-code" autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} disabled={action.pending} onChange={(event) => setCode(event.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? "magic-authenticator-error" : undefined} />
      </div>
      {error ? <p id="magic-authenticator-error" role="alert" className="text-sm text-destructive">{error}</p> : null}
      <Button type="submit" className="min-h-11 w-full" loading={action.pending} loadingLabel="Verifying sign-in…">Verify and sign in</Button>
      <Button type="button" variant="outline" className="min-h-11 w-full" disabled={action.pending} onClick={onBack}>Back to email sign-in</Button>
    </form>
  );
}
